'use client';

import React from 'react';
import styled from 'styled-components';

/* ═══════════════════════════════════════════
   Unified widget previews for showcase cards.
   Clean, geometric, accent-colored designs.
   ═══════════════════════════════════════════ */

const accent = 'var(--accent, #4c4fff)';
const accentLight = 'var(--accent-light, rgba(76,79,255,0.08))';

const PreviewWrap = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

/* ─── BEFORE & AFTER SLIDER ─── */

const SliderWrap = styled(PreviewWrap)`
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  background: linear-gradient(135deg, #e2e8f0, #cbd5e1);

  .side-left {
    position: absolute; inset: 0; right: 40%;
    background: linear-gradient(145deg, #94a3b8 0%, #64748b 100%);
    display: flex; align-items: center; justify-content: center;
  }
  .side-right {
    position: absolute; inset: 0; left: 60%;
    background: linear-gradient(145deg, ${accent} 0%, color-mix(in srgb, ${accent} 70%, #000 30%) 100%);
    display: flex; align-items: center; justify-content: center;
  }
  .label {
    font-size: 9px; font-weight: 800; color: #fff;
    letter-spacing: 0.12em; text-transform: uppercase;
    text-shadow: 0 1px 4px rgba(0,0,0,0.3);
  }
  .divider {
    position: absolute; left: 60%; top: 0; bottom: 0; width: 2px; background: #fff;
  }
  .handle {
    position: absolute; left: 60%; top: 50%; transform: translate(-50%,-50%);
    width: 26px; height: 26px; background: #fff; border-radius: 50%;
    box-shadow: 0 2px 10px rgba(0,0,0,0.2);
    display: flex; align-items: center; justify-content: center;
    z-index: 1;
  }
  .handle-arrows {
    display: flex; gap: 2px; color: #64748b; font-size: 9px; font-weight: 700;
  }
`;

export function PreviewBeforeAfter() {
  return (
    <SliderWrap>
      <div className="side-left"><span className="label">Before</span></div>
      <div className="side-right"><span className="label">After</span></div>
      <div className="divider" />
      <div className="handle"><span className="handle-arrows">&lsaquo; &rsaquo;</span></div>
    </SliderWrap>
  );
}

/* ─── REVIEWS ─── */

const ReviewsWrap = styled(PreviewWrap)`
  gap: 6px; justify-content: center;
`;

const ReviewCard = styled.div<{ $dim?: boolean }>`
  background: #fff;
  border: 1px solid ${({ $dim }) => $dim ? '#e2e8f0' : accent};
  border-radius: 10px;
  padding: 10px 12px;
  opacity: ${({ $dim }) => $dim ? 0.55 : 1};
  transform: ${({ $dim }) => $dim ? 'scale(0.97)' : 'none'};
  display: flex; gap: 10px; align-items: flex-start;

  .avatar {
    width: 28px; height: 28px; border-radius: 50%; flex-shrink: 0;
    background: ${({ $dim }) => $dim ? '#e2e8f0' : accentLight};
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 700; color: ${({ $dim }) => $dim ? '#94a3b8' : accent};
  }
  .body { min-width: 0; }
  .stars { color: #f59e0b; font-size: 10px; letter-spacing: 1px; }
  .text { font-size: 9.5px; color: #475569; line-height: 1.35; margin-top: 2px; }
  .author { font-size: 8.5px; font-weight: 700; color: #1e293b; margin-top: 2px; }
  .source { font-size: 8px; color: #94a3b8; }
`;

export function PreviewReviews() {
  return (
    <ReviewsWrap>
      <ReviewCard>
        <div className="avatar">SM</div>
        <div className="body">
          <div className="stars">&#9733;&#9733;&#9733;&#9733;&#9733;</div>
          <div className="text">Fixed our burst pipe at 11pm. Brilliant!</div>
          <div className="author">Sarah M. <span className="source">&middot; Google</span></div>
        </div>
      </ReviewCard>
      <ReviewCard $dim>
        <div className="avatar">JK</div>
        <div className="body">
          <div className="stars">&#9733;&#9733;&#9733;&#9733;&#9733;</div>
          <div className="text">Best HVAC company in the city.</div>
          <div className="author">James K. <span className="source">&middot; Yelp</span></div>
        </div>
      </ReviewCard>
    </ReviewsWrap>
  );
}

/* ─── PRICING TABLE ─── */

const PricingWrap = styled(PreviewWrap)`
  flex-direction: row; gap: 6px; align-items: stretch; justify-content: center;
`;

const PriceCol = styled.div<{ $featured?: boolean }>`
  flex: 1; text-align: center; border-radius: 10px; padding: 12px 6px 10px;
  position: relative;
  background: ${({ $featured }) => $featured ? accent : '#fff'};
  color: ${({ $featured }) => $featured ? '#fff' : '#1e293b'};
  border: 1.5px solid ${({ $featured }) => $featured ? accent : '#e2e8f0'};
  display: flex; flex-direction: column; justify-content: center; gap: 2px;
  ${({ $featured }) => $featured && 'transform: scaleY(1.03); z-index: 1; box-shadow: 0 4px 16px rgba(0,0,0,0.12);'}

  .badge {
    position: absolute; top: -8px; left: 50%; transform: translateX(-50%);
    background: #fff; color: ${accent}; font-size: 7px; font-weight: 800;
    padding: 2px 8px; border-radius: 100px; white-space: nowrap;
    box-shadow: 0 1px 4px rgba(0,0,0,0.1);
  }
  .tier { font-size: 9px; font-weight: 700; opacity: 0.8; }
  .amount { font-size: 20px; font-weight: 900; line-height: 1.1; }
  .period { font-size: 7px; opacity: 0.6; }
  .feat { font-size: 7.5px; opacity: 0.65; margin-top: 3px; line-height: 1.4; }
`;

export function PreviewPricing() {
  return (
    <PricingWrap>
      <PriceCol>
        <div className="tier">Basic</div>
        <div className="amount">$89</div>
        <div className="period">/visit</div>
        <div className="feat">1 tech &middot; Standard</div>
      </PriceCol>
      <PriceCol $featured>
        <div className="badge">Popular</div>
        <div className="tier">Pro</div>
        <div className="amount">$149</div>
        <div className="period">/month</div>
        <div className="feat">Priority &middot; Free parts</div>
      </PriceCol>
      <PriceCol>
        <div className="tier">Complete</div>
        <div className="amount">$249</div>
        <div className="period">/month</div>
        <div className="feat">24/7 &middot; All systems</div>
      </PriceCol>
    </PricingWrap>
  );
}

/* ─── FAQ ACCORDION ─── */

const FaqWrap = styled(PreviewWrap)`
  gap: 5px; justify-content: center;
`;

const FaqItem = styled.div<{ $open?: boolean }>`
  background: ${({ $open }) => $open ? accentLight : '#fff'};
  border: 1.5px solid ${({ $open }) => $open ? accent : '#e2e8f0'};
  border-radius: 8px;
  padding: 9px 12px;
  display: flex; justify-content: space-between; align-items: center;
  font-size: 10.5px; font-weight: 600;
  color: ${({ $open }) => $open ? accent : '#374151'};

  .chevron { font-size: 10px; opacity: 0.6; }
`;

const FaqAnswer = styled.div`
  background: ${accentLight};
  border: 1.5px solid color-mix(in srgb, ${accent} 25%, transparent 75%);
  border-top: none;
  border-radius: 0 0 8px 8px;
  padding: 7px 12px;
  font-size: 9px; color: #64748b; line-height: 1.5;
  margin-top: -5px;
`;

export function PreviewFaq() {
  return (
    <FaqWrap>
      <div>
        <FaqItem $open>Same-day service? <span className="chevron">&#9650;</span></FaqItem>
        <FaqAnswer>Yes! Call before noon for guaranteed same-day arrival.</FaqAnswer>
      </div>
      <FaqItem>What areas do you cover? <span className="chevron">&#9660;</span></FaqItem>
      <FaqItem>Warranty on repairs? <span className="chevron">&#9660;</span></FaqItem>
    </FaqWrap>
  );
}

/* ─── OPENING HOURS ─── */

const HoursWrap = styled(PreviewWrap)`
  background: #fff;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;

  .header {
    background: ${accent};
    color: #fff;
    padding: 8px 12px;
    font-size: 11px; font-weight: 700;
    display: flex; align-items: center; gap: 6px;
  }

  .row {
    display: flex; justify-content: space-between;
    padding: 5px 12px; font-size: 10px;
    border-bottom: 1px solid #f1f5f9; color: #475569;
    &:last-child { border-bottom: none; }
  }
  .row.today { background: ${accentLight}; font-weight: 600; }
  .today-dot {
    font-size: 7px; background: #22c55e; color: #fff;
    padding: 1px 6px; border-radius: 3px; margin-left: 4px; font-weight: 700;
  }
  .time { color: ${accent}; font-weight: 600; }
  .closed { color: #ef4444; font-weight: 600; }
`;

export function PreviewHours() {
  return (
    <HoursWrap>
      <div className="header">&#128339; Business Hours</div>
      <div className="row today">
        <span>Monday <span className="today-dot">NOW</span></span>
        <span className="time">8 AM &ndash; 6 PM</span>
      </div>
      <div className="row"><span>Tuesday</span><span className="time">8 AM &ndash; 6 PM</span></div>
      <div className="row"><span>Wednesday</span><span className="time">8 AM &ndash; 6 PM</span></div>
      <div className="row"><span>Saturday</span><span className="time">9 AM &ndash; 3 PM</span></div>
      <div className="row"><span>Sunday</span><span className="closed">Closed</span></div>
    </HoursWrap>
  );
}

/* ─── AI CHATBOT ─── */

const ChatWrap = styled(PreviewWrap)`
  gap: 6px; justify-content: flex-end;
`;

const Bubble = styled.div<{ $in?: boolean }>`
  max-width: 82%;
  padding: 9px 13px;
  font-size: 10px; line-height: 1.4;
  border-radius: ${({ $in }) => $in ? '14px 14px 14px 4px' : '14px 14px 4px 14px'};
  background: ${({ $in }) => $in ? '#fff' : accent};
  color: ${({ $in }) => $in ? '#374151' : '#fff'};
  border: ${({ $in }) => $in ? '1.5px solid #e2e8f0' : 'none'};
  align-self: ${({ $in }) => $in ? 'flex-start' : 'flex-end'};
  ${({ $in }) => !$in && 'box-shadow: 0 2px 8px rgba(0,0,0,0.1);'}
`;

const BotBadge = styled.div`
  display: flex; align-items: center; gap: 5px; align-self: flex-start;
  font-size: 9px; font-weight: 700; color: #64748b;
  .dot { width: 7px; height: 7px; border-radius: 50%; background: #22c55e; }
`;

const TypingDots = styled.div`
  align-self: flex-end;
  background: ${accent};
  padding: 8px 16px;
  border-radius: 14px 14px 4px 14px;
  display: flex; gap: 4px; align-items: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  .dot {
    width: 5px; height: 5px; border-radius: 50%; background: rgba(255,255,255,0.6);
    &:nth-child(2) { opacity: 0.8; }
    &:nth-child(3) { opacity: 0.5; }
  }
`;

export function PreviewChatbot() {
  return (
    <ChatWrap>
      <BotBadge><span className="dot" /> AI Assistant &middot; Online</BotBadge>
      <Bubble $in>Do you offer same-day plumbing service?</Bubble>
      <Bubble>Yes! We have technicians available today. What&apos;s the issue?</Bubble>
      <Bubble $in>Leaking pipe under the kitchen sink</Bubble>
      <TypingDots><span className="dot" /><span className="dot" /><span className="dot" /></TypingDots>
    </ChatWrap>
  );
}

/* ─── CALCULATOR ─── */

const CalcWrap = styled(PreviewWrap)`
  background: #fff; border: 1.5px solid #e2e8f0; border-radius: 10px; overflow: hidden;

  .calc-header { background: ${accent}; color: #fff; padding: 8px 12px; font-size: 11px; font-weight: 700; }
  .calc-body { padding: 10px 12px; display: flex; flex-direction: column; gap: 6px; flex: 1; }
  .calc-field { display: flex; flex-direction: column; gap: 2px;
    label { font-size: 8px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; }
    .input { background: #f8faff; border: 1px solid #e2e8f0; border-radius: 5px; padding: 5px 8px; font-size: 11px; color: #1e293b; font-weight: 600; }
  }
  .calc-result { background: ${accentLight}; border: 1.5px solid ${accent}; border-radius: 8px; padding: 8px; text-align: center; margin-top: auto;
    .label { font-size: 8px; color: #64748b; font-weight: 600; text-transform: uppercase; }
    .value { font-size: 18px; font-weight: 900; color: ${accent}; }
    .sub { font-size: 8px; color: #94a3b8; }
  }
`;

export function PreviewCalculator() {
  return (
    <CalcWrap>
      <div className="calc-header">&#128200; Mortgage Calculator</div>
      <div className="calc-body">
        <div className="calc-field"><label>Purchase Price</label><div className="input">$425,000</div></div>
        <div className="calc-field"><label>Down Payment</label><div className="input">$85,000 (20%)</div></div>
        <div className="calc-field"><label>Interest Rate</label><div className="input">6.5%</div></div>
        <div className="calc-result">
          <div className="label">Monthly Payment</div>
          <div className="value">$2,149</div>
          <div className="sub">30-year fixed</div>
        </div>
      </div>
    </CalcWrap>
  );
}

/* ─── QUIZ ─── */

const QuizWrap = styled(PreviewWrap)`
  background: #fff; border: 1.5px solid #e2e8f0; border-radius: 10px; overflow: hidden;

  .quiz-header { background: ${accent}; color: #fff; padding: 8px 12px; font-size: 10px; font-weight: 700; display: flex; justify-content: space-between; }
  .quiz-body { padding: 10px 12px; display: flex; flex-direction: column; gap: 6px; flex: 1; justify-content: center; }
  .quiz-q { font-size: 11px; font-weight: 700; color: #1e293b; margin-bottom: 2px; }
  .quiz-option { background: #f8faff; border: 1.5px solid #e2e8f0; border-radius: 6px; padding: 6px 10px; font-size: 10px; color: #475569; cursor: pointer;
    &.selected { border-color: ${accent}; background: ${accentLight}; color: ${accent}; font-weight: 600; }
  }
  .quiz-progress { height: 3px; background: #e2e8f0; border-radius: 2px; margin-top: auto;
    .bar { height: 100%; width: 60%; background: ${accent}; border-radius: 2px; }
  }
`;

export function PreviewQuiz() {
  return (
    <QuizWrap>
      <div className="quiz-header"><span>Home Affordability Quiz</span><span>Q3 of 5</span></div>
      <div className="quiz-body">
        <div className="quiz-q">What is your annual household income?</div>
        <div className="quiz-option">Under $75,000</div>
        <div className="quiz-option selected">$75,000 - $150,000</div>
        <div className="quiz-option">$150,000+</div>
        <div className="quiz-progress"><div className="bar" /></div>
      </div>
    </QuizWrap>
  );
}

/* ─── COMPARISON TABLES ─── */

const CompWrap = styled(PreviewWrap)`
  background: #fff; border: 1.5px solid #e2e8f0; border-radius: 10px; overflow: hidden;

  .comp-header { display: grid; grid-template-columns: 1.2fr 1fr 1fr; background: ${accent}; color: #fff; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
    span { padding: 7px 8px; }
  }
  .comp-row { display: grid; grid-template-columns: 1.2fr 1fr 1fr; border-bottom: 1px solid #f1f5f9; font-size: 9.5px;
    span { padding: 6px 8px; color: #475569; }
    .label { font-weight: 600; color: #1e293b; }
    .highlight { font-weight: 700; color: ${accent}; }
    &:last-child { border-bottom: none; }
  }
`;

export function PreviewComparison() {
  return (
    <CompWrap>
      <div className="comp-header"><span>Feature</span><span>Renting</span><span>Buying</span></div>
      <div className="comp-row"><span className="label">Monthly Cost</span><span>$2,100</span><span className="highlight">$2,149</span></div>
      <div className="comp-row"><span className="label">5-Year Total</span><span>$126,000</span><span className="highlight">$128,940</span></div>
      <div className="comp-row"><span className="label">Equity Built</span><span>$0</span><span className="highlight">$68,400</span></div>
      <div className="comp-row"><span className="label">Tax Benefits</span><span>None</span><span className="highlight">$4,200/yr</span></div>
      <div className="comp-row"><span className="label">10-Year Net</span><span>-$252,000</span><span className="highlight">+$124,800</span></div>
    </CompWrap>
  );
}

/* ─── BOOKING ─── */

const BookWrap = styled(PreviewWrap)`
  background: #fff; border: 1.5px solid #e2e8f0; border-radius: 10px; overflow: hidden;

  .book-header { background: ${accent}; color: #fff; padding: 8px 12px; font-size: 11px; font-weight: 700; }
  .book-body { padding: 10px 12px; display: flex; flex-direction: column; gap: 6px; flex: 1; }
  .book-cal { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; text-align: center; font-size: 8px; color: #94a3b8;
    .day-name { font-weight: 700; color: #64748b; padding: 2px 0; }
    .day { padding: 3px 0; border-radius: 4px; }
    .day.available { color: #1e293b; font-weight: 600; }
    .day.selected { background: ${accent}; color: #fff; font-weight: 700; }
  }
  .book-slots { display: flex; gap: 4px; flex-wrap: wrap;
    .slot { padding: 4px 8px; border: 1px solid #e2e8f0; border-radius: 5px; font-size: 9px; color: #475569; font-weight: 600;
      &.active { border-color: ${accent}; background: ${accentLight}; color: ${accent}; }
    }
  }
  .book-btn { background: ${accent}; color: #fff; border: none; border-radius: 6px; padding: 6px; font-size: 10px; font-weight: 700; text-align: center; margin-top: auto; }
`;

export function PreviewBooking() {
  return (
    <BookWrap>
      <div className="book-header">&#128197; Book a Showing</div>
      <div className="book-body">
        <div className="book-cal">
          <span className="day-name">M</span><span className="day-name">T</span><span className="day-name">W</span><span className="day-name">T</span><span className="day-name">F</span><span className="day-name">S</span><span className="day-name">S</span>
          <span className="day">14</span><span className="day available">15</span><span className="day available">16</span><span className="day selected">17</span><span className="day available">18</span><span className="day">19</span><span className="day">20</span>
        </div>
        <div className="book-slots">
          <span className="slot">9:00 AM</span>
          <span className="slot active">10:30 AM</span>
          <span className="slot">1:00 PM</span>
          <span className="slot">3:30 PM</span>
        </div>
        <div className="book-btn">Confirm Showing</div>
      </div>
    </BookWrap>
  );
}

/* ─── TESTIMONIALS ─── */

const TestWrap = styled(PreviewWrap)`
  gap: 6px; justify-content: center;
`;

const TestCard = styled.div<{ $dim?: boolean }>`
  background: #fff; border: 1.5px solid ${({ $dim }) => $dim ? '#e2e8f0' : accent}; border-radius: 10px; padding: 10px 12px;
  opacity: ${({ $dim }) => $dim ? 0.55 : 1};
  transform: ${({ $dim }) => $dim ? 'scale(0.97)' : 'none'};

  .stars { color: #f59e0b; font-size: 10px; letter-spacing: 1px; margin-bottom: 3px; }
  .text { font-size: 9.5px; color: #475569; line-height: 1.35; font-style: italic; }
  .author { font-size: 9px; font-weight: 700; color: #1e293b; margin-top: 4px; }
  .role { font-size: 8px; color: #94a3b8; }
`;

export function PreviewTestimonials() {
  return (
    <TestWrap>
      <TestCard>
        <div className="stars">&#9733;&#9733;&#9733;&#9733;&#9733;</div>
        <div className="text">&ldquo;Found our dream home in 2 weeks. Best agent experience ever.&rdquo;</div>
        <div className="author">Sarah &amp; Tom P.</div>
        <div className="role">First-time buyers</div>
      </TestCard>
      <TestCard $dim>
        <div className="stars">&#9733;&#9733;&#9733;&#9733;&#9733;</div>
        <div className="text">&ldquo;Sold above asking in 5 days. Incredible marketing.&rdquo;</div>
        <div className="author">Michael R.</div>
        <div className="role">Home seller</div>
      </TestCard>
    </TestWrap>
  );
}

/* ─── COUNTDOWN BAR ─── */

const CountdownWrap = styled(PreviewWrap)`
  gap: 8px; justify-content: center; align-items: center;
`;

const CountdownBar = styled.div`
  width: 100%; background: linear-gradient(135deg, ${accent}, color-mix(in srgb, ${accent} 80%, #000 20%));
  border-radius: 10px; padding: 12px 16px; color: #fff; text-align: center;

  .cta-text { font-size: 11px; font-weight: 700; margin-bottom: 8px; }
  .timer { display: flex; gap: 8px; justify-content: center; }
  .time-block { background: rgba(255,255,255,0.15); border-radius: 6px; padding: 6px 10px; min-width: 40px;
    .num { font-size: 16px; font-weight: 900; line-height: 1; }
    .label { font-size: 7px; text-transform: uppercase; opacity: 0.7; letter-spacing: 0.06em; }
  }
  .sub { font-size: 9px; opacity: 0.7; margin-top: 6px; }
`;

export function PreviewCountdown() {
  return (
    <CountdownWrap>
      <CountdownBar>
        <div className="cta-text">Spring Sale Ends In</div>
        <div className="timer">
          <div className="time-block"><div className="num">02</div><div className="label">Days</div></div>
          <div className="time-block"><div className="num">14</div><div className="label">Hrs</div></div>
          <div className="time-block"><div className="num">37</div><div className="label">Min</div></div>
          <div className="time-block"><div className="num">09</div><div className="label">Sec</div></div>
        </div>
        <div className="sub">Limited time offer. Don&apos;t miss out.</div>
      </CountdownBar>
    </CountdownWrap>
  );
}

/* ─── ANIMATED NUMBER COUNTER ─── */

const NumbersWrap = styled(PreviewWrap)`
  flex-direction: row; gap: 8px; align-items: stretch; justify-content: center;
`;

const NumberBlock = styled.div`
  flex: 1; background: #fff; border: 1.5px solid #e2e8f0; border-radius: 10px;
  padding: 12px 8px; text-align: center; display: flex; flex-direction: column; justify-content: center;

  .num { font-size: 20px; font-weight: 900; color: ${accent}; line-height: 1; }
  .suffix { font-size: 14px; font-weight: 900; color: ${accent}; }
  .label { font-size: 8px; color: #64748b; margin-top: 4px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; }
`;

export function PreviewNumbers() {
  return (
    <NumbersWrap>
      <NumberBlock><div><span className="num">2,500</span><span className="suffix">+</span></div><div className="label">Clients Served</div></NumberBlock>
      <NumberBlock><div><span className="num">99.9</span><span className="suffix">%</span></div><div className="label">Uptime</div></NumberBlock>
      <NumberBlock><div><span className="num">4.8</span><span className="suffix">/5</span></div><div className="label">Rating</div></NumberBlock>
    </NumbersWrap>
  );
}

/* ─── SPINNING WHEEL ─── */

const WheelWrap = styled(PreviewWrap)`
  align-items: center; justify-content: center; position: relative;

  .wheel {
    width: 120px; height: 120px; border-radius: 50%; position: relative;
    background: conic-gradient(
      ${accent} 0deg 60deg, #fff 60deg 120deg,
      ${accent} 120deg 180deg, #fff 180deg 240deg,
      ${accent} 240deg 300deg, #fff 300deg 360deg
    );
    border: 3px solid ${accent};
    box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  }
  .center { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%);
    width: 28px; height: 28px; background: #fff; border-radius: 50%;
    border: 2px solid ${accent}; display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 900; color: ${accent}; box-shadow: 0 2px 6px rgba(0,0,0,0.15);
  }
  .pointer { position: absolute; top: -6px; left: 50%; transform: translateX(-50%);
    width: 0; height: 0; border-left: 7px solid transparent; border-right: 7px solid transparent;
    border-top: 12px solid ${accent}; filter: drop-shadow(0 1px 2px rgba(0,0,0,0.2));
  }
  .label { font-size: 10px; font-weight: 700; color: #1e293b; margin-top: 8px; text-align: center; }
  .sub-label { font-size: 8px; color: #94a3b8; text-align: center; }
`;

export function PreviewSpinWheel() {
  return (
    <WheelWrap>
      <div style={{ position: 'relative' }}>
        <div className="pointer" />
        <div className="wheel"><div className="center">SPIN</div></div>
      </div>
      <div className="label">Spin to Win!</div>
      <div className="sub-label">Get up to 30% off</div>
    </WheelWrap>
  );
}

/* ─── COUPON BAR ─── */

const CouponWrap = styled(PreviewWrap)`
  gap: 8px; justify-content: center; align-items: center;
`;

const CouponBarStyled = styled.div`
  width: 100%; background: linear-gradient(135deg, ${accent}, color-mix(in srgb, ${accent} 70%, #000 30%));
  border-radius: 10px; padding: 14px 16px; color: #fff; text-align: center;

  .headline { font-size: 12px; font-weight: 800; margin-bottom: 6px; }
  .code-row { display: flex; align-items: center; justify-content: center; gap: 8px; }
  .code { background: rgba(255,255,255,0.2); border: 1.5px dashed rgba(255,255,255,0.5);
    border-radius: 6px; padding: 5px 14px; font-size: 13px; font-weight: 900; letter-spacing: 0.1em; }
  .copy-btn { background: #fff; color: ${accent}; border: none; border-radius: 5px;
    padding: 5px 10px; font-size: 9px; font-weight: 700; cursor: pointer; }
  .sub { font-size: 8px; opacity: 0.7; margin-top: 6px; }
`;

export function PreviewCouponBar() {
  return (
    <CouponWrap>
      <CouponBarStyled>
        <div className="headline">Use code for 15% off your first order</div>
        <div className="code-row">
          <span className="code">WELCOME15</span>
          <button className="copy-btn">COPY</button>
        </div>
        <div className="sub">Valid for new customers only. Expires in 48 hours.</div>
      </CouponBarStyled>
    </CouponWrap>
  );
}

/* ─── PREVIEW MAP (slug → component) ─── */

export const WIDGET_PREVIEW_MAP: Record<string, React.FC> = {
  'before-after': PreviewBeforeAfter,
  'all-in-one-reviews': PreviewReviews,
  'pricing-tables': PreviewPricing,
  'faq': PreviewFaq,
  'business-hours': PreviewHours,
  'ai-chatbot': PreviewChatbot,
  'calculator': PreviewCalculator,
  'quiz': PreviewQuiz,
  'comparison-tables': PreviewComparison,
  'booking': PreviewBooking,
  'testimonials': PreviewTestimonials,
  'countdown-bar': PreviewCountdown,
  'numbers-counter': PreviewNumbers,
  'spinning-wheel': PreviewSpinWheel,
  'coupon-bar': PreviewCouponBar,
};
